import {createFragmentContainer, graphql} from 'react-relay';
import {decompressAggregatedData, decompressData} from '../util/DataDecompressUtil';
import {getCurrency, TABLE_TYPE} from '../util/BudgetUtils';

function duplicateTotalsRow(rows) {
	const totalRow = rows[rows.length - 1];
	if (totalRow) {
		rows.push(totalRow);
	}
}

const RevenueRecognitionDetailsLoader = ({
	viewer,
	aggregateLevel,
	revenueAggregates,
	costAggregates,
	allAggregates,
	tableType,
	children,
}) => {
	const {aggregatedFinancialNumbers, financialNumbers} = viewer.project;
	const currency = getCurrency(viewer.company, viewer.project);
	const processedData = decompressData(
		aggregatedFinancialNumbers,
		financialNumbers,
		currency,
		aggregateLevel,
		allAggregates,
		false
	);
	const revenueData = {
		rows: processedData.rows.filter(row => revenueAggregates.includes(row.aggregate)),
		columns: processedData.columns,
	};
	const costData = {
		rows: processedData.rows.filter(row => costAggregates.includes(row.aggregate)),
		columns: processedData.columns,
	};

	const accumulatedNumbers = decompressAggregatedData(aggregatedFinancialNumbers, allAggregates);

	const recognitionAmountAccumulatedRow = accumulatedNumbers.map(financialNumbers => {
		return {value: financialNumbers.recognitionAmountAccumulated, startDate: financialNumbers.date};
	});
	const recognitionPercentageAccumulatedRow = accumulatedNumbers.map(financialNumbers => {
		return {
			value:
				tableType === TABLE_TYPE.TIME_REVENUE_RECOGNITION
					? financialNumbers.recognitionPercentageAccumulated
					: tableType === TABLE_TYPE.EXPENSE_REVENUE_RECOGNITION
					? financialNumbers.expenseRevenuePercentageAccumulated
					: 0,
			startDate: financialNumbers.date,
		};
	});
	const billableTimeAccumulatedRow = accumulatedNumbers.map(financialNumbers => {
		return {value: financialNumbers.billableTimeAccumulated, startDate: financialNumbers.date};
	});
	duplicateTotalsRow(recognitionAmountAccumulatedRow);
	duplicateTotalsRow(recognitionPercentageAccumulatedRow);
	duplicateTotalsRow(billableTimeAccumulatedRow);

	const estimatedCostAccumulatedRow = accumulatedNumbers.map(financialNumbers => {
		return {value: financialNumbers.estimatedCostAccumulated, startDate: financialNumbers.date};
	});
	const estimatedCostPercentageAccumulatedRow = accumulatedNumbers.map(financialNumbers => {
		return {
			value:
				tableType === TABLE_TYPE.TIME_REVENUE_RECOGNITION
					? financialNumbers.estimatedCostPercentageAccumulated
					: tableType === TABLE_TYPE.EXPENSE_REVENUE_RECOGNITION
					? financialNumbers.expenseCostPercentageAccumulated
					: 0,
			startDate: financialNumbers.date,
		};
	});
	duplicateTotalsRow(estimatedCostAccumulatedRow);
	duplicateTotalsRow(estimatedCostPercentageAccumulatedRow);

	const tableSuffix =
		tableType === TABLE_TYPE.TIME_REVENUE_RECOGNITION
			? '.recognition_time_table'
			: tableType === TABLE_TYPE.EXPENSE_REVENUE_RECOGNITION
			? '.recognition_expense_table'
			: '';

	const revenueRecognitionCummulativeTable = {
		headlineTextId: 'project_budget.recognition_cummulative_revenue_table_headline' + tableSuffix,
		showHeaderRow: true,
		columns: [...revenueData.columns],
		rows: [
			{
				dataArray: recognitionPercentageAccumulatedRow,
				currency,
				aggregate: 'recognitionPercentageAccumulated',
			},
			{
				dataArray: recognitionAmountAccumulatedRow,
				currency,
				aggregate: 'recognitionAmountAccumulated',
			},
		],
	};

	const billableTimeIndex = revenueData.rows.findIndex(row => row.aggregate === 'billableTime');
	if (billableTimeIndex !== -1) {
		revenueData.rows.splice(billableTimeIndex, 0, {
			dataArray: billableTimeAccumulatedRow,
			currency,
			aggregate: 'billableTimeAccumulated',
		});
		revenueData.rows.splice(billableTimeIndex, 0, {dataArray: []});
	}
	const timeMaterialTimeMarginIndex = revenueData.rows.findIndex(row => row.aggregate === 'forecastMargin');
	if (timeMaterialTimeMarginIndex !== -1) {
		revenueData.rows.splice(timeMaterialTimeMarginIndex + 1, 0, {dataArray: []});
	}

	const revenueRecognitionTable = {
		headlineTextId: 'project_budget.recognition_revenue_table_headline' + tableSuffix,
		showHeaderRow: false,
		columns: [...revenueData.columns],
		rows: [...revenueData.rows],
	};

	const costTable = {
		headlineTextId: 'project_budget.recognition_cost_table_headline' + tableSuffix,
		showHeaderRow: false,
		columns: [...costData.columns],
		rows: [
			{
				dataArray: estimatedCostPercentageAccumulatedRow,
				currency,
				aggregate: 'estimatedCostPercentageAccumulated',
			},
			{
				dataArray: estimatedCostAccumulatedRow,
				currency,
				aggregate: 'estimatedCostAccumulated',
			},
			...costData.rows,
		],
	};

	const result = {
		tables: [revenueRecognitionCummulativeTable, revenueRecognitionTable, costTable],
	};
	return children(result);
};

export const revenueRecognitionDetailsLoaderQuery = graphql`
	query RevenueRecognitionDetailsLoader_Query(
		$projectId: ID!
		$startYear: Int
		$startMonth: Int
		$startDay: Int
		$endYear: Int
		$endMonth: Int
		$endDay: Int
		$aggregateLevel: AggregateLevel!
		$aggregates: [FinancialAggregatedNumber]!
		$searchQuery: TaskSearchQueryType
	) {
		viewer {
			actualPersonId
			component(name: "revenue_recognition_details_loader")
			...RevenueRecognitionDetailsLoader_viewer
				@arguments(
					projectId: $projectId
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: $aggregateLevel
					aggregates: $aggregates
					searchQuery: $searchQuery
				)
		}
	}
`;

export default createFragmentContainer(RevenueRecognitionDetailsLoader, {
	viewer: graphql`
		fragment RevenueRecognitionDetailsLoader_viewer on Viewer
		@argumentDefinitions(
			projectId: {type: "ID!"}
			startYear: {type: "Int"}
			startMonth: {type: "Int"}
			startDay: {type: "Int"}
			endYear: {type: "Int"}
			endMonth: {type: "Int"}
			endDay: {type: "Int"}
			aggregateLevel: {type: "AggregateLevel!"}
			aggregates: {type: "[FinancialAggregatedNumber]!"}
			searchQuery: {type: "TaskSearchQueryType"}
		) {
			id
			company {
				currency
			}
			project(internalId: $projectId) {
				rateCard {
					currency
				}
				aggregatedFinancialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: $aggregateLevel
					aggregates: $aggregates
					convertToProjectCurrency: true
					addAccumulatedNumbers: true
					searchQuery: $searchQuery
				)
				financialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $searchQuery
				) {
					recognitionAmount
					recognitionPercentage
					expenseRevenuePercentage
					recognitionProfit
					recognitionProfitPercentage
					billableTime
					surplus
					surplusPercentage
					estimatedCost
					estimatedCostPercentage
					expenseCostPercentage
				}
			}
		}
	`,
});
